// importing functionality from other places and libraries
import {fromJS} from 'immutable'
import {getColorsHSLAFromTheme, injectCurrentStateNameIfNeeded, isThemeString} from './utilsColorTheme'
import { tableWithSameValue } from '../utils'

// constants
import { BASE_MAP_LAYERS } from '../constants'

const defaultCartoLayerInfo = {
    "id": "",
    "source-layer": "",
    // "source": "cartoSource",
    "type": "",
    "layout": {},
    "paint": {}
}

const DEFAULT_OUTLINE_COLOR = "hsla(0,0%,100%, 0)" // white
const POLYGON_HOVER_OUTLINE_COLOR = "black"
const TRANSPARENT = "hsla(0,0%,100%, 0)"

// =====================================================================
// class LayerPaintProperty
// Represents the paint properties for a layer in a story scene layer including filters, etc -- eg storymap.yaml
// =====================================================================
class LayerPaintProperty {
    constructor(layerInfo, location) {
        this._layerInfo = layerInfo
        this._paint = {}
        this.regeneratePaintProperties(location)
    }

    get paint() { return this._paint }

    // we recreate the paint styling for all the layers, ie formatting location codes and color themes
    // this is for new locations ie switching states, so we want the paint styles filters (masks, etc) to update too
    regeneratePaintProperties = (location = null) => {
        this._convertColorThemes()
        location && this._convertLocationCodes(location)
        this._generatePaintProperties()
    }

    // convert color themes to colors
    _convertColorThemes = () => {
        const { color } = this._layerInfo
        for (var colorProp in color) {
            if (typeof color[colorProp] === 'string' && isThemeString(color[colorProp])) {
                if (colorProp === 'fillBins') {
                    const bins = this._layerInfo.fieldBins ? this._layerInfo.fieldBins : 1
                    const fillAlpha = color.fillAlpha || 1
                    color[colorProp] = getColorsHSLAFromTheme(color[colorProp], bins, fillAlpha)
                } else {
                    color[colorProp] = getColorsHSLAFromTheme(color[colorProp])
                }
            }
        }
        this._layerInfo.updateColor(color)
        
        // checking custom filters as well
        if (this._layerInfo.customFilters) {
            const formattedFilters = this._layerInfo.customFilters.reduce((accumulated, logic) => {
                return (isThemeString(logic)) ? [...accumulated, getColorsHSLAFromTheme(logic)] : [...accumulated, logic]
            }, [])
            this._layerInfo.updateFilters(formattedFilters)
        }
    }

    // this converts any location codes in the YAML specs (eg state_name) to the current location's
    _convertLocationCodes = location => {
        if (this._layerInfo.customFilters) {
            const formattedFilters = this._layerInfo.customFilters.reduce((accumulated, logic) => {
                const { nullFilterExpression = [], formattedExpression } = injectCurrentStateNameIfNeeded(logic, location)
                return [...accumulated, ...nullFilterExpression, formattedExpression]
            }, [])
            this._layerInfo.updateFilters(formattedFilters)
        }
    }

    // generate the paint property used for rendering by mapgl layer
    _generatePaintProperties = () => {
        // get the field, or use a custom expression if given
        var fieldGet = null
        if (this._layerInfo.fieldName)
            fieldGet = ["to-number", ["get", this._layerInfo.fieldName]]

        if (this._layerInfo.customField)
            fieldGet = this._layerInfo.customField

        // use fill color if specified
        var paintColor = []
        if (this._layerInfo.color.fillColor) {
            paintColor = this._layerInfo.color.fillColor
        } else {
            // otherwise try to generate choropleth steps if we have a field
            paintColor = []
            if (fieldGet && fieldGet.length) {
                const [lowerBound, upperBound] = this._layerInfo.fieldRange
                const bins = this._layerInfo.fieldBins || 1 // default to one bin
                const stepSize = (upperBound-lowerBound) / bins

                paintColor = ["step", fieldGet]
                for (var j=0; j<bins; j++) {
                    // use either fillBins or transparent if null
                    const stepColor = this._layerInfo.color.fillBins ? this._layerInfo.color.fillBins[j] : TRANSPARENT
                    paintColor.push(stepColor)

                    if (j < (bins-1)) // skip the last step, no need
                        paintColor.push(lowerBound + (stepSize * j))
                }
            }
        }

        // outline color
        const outlineColor = this._layerInfo.color.outline ? this._layerInfo.color.outline : DEFAULT_OUTLINE_COLOR

        // lastly apply any custom expressions logic directly from YAML
        const customFilters = this._layerInfo.customFilters
        const filters = (customFilters && customFilters.length) ? ["case", ...customFilters] : false

        // combine everything together
        this._paint = {
            "fill-outline-color": outlineColor,
            "fill-color": filters ? [...filters, paintColor] : paintColor
        }
    }
}

// =====================================================================
// class CartoLayerDefaultInfo
// Represents the Mapbox styling for a layer by default based on info from the master list of possible layers
// =====================================================================
class CartoLayerDefaultInfo {
    constructor(cartoInfo, data) {
        // settings
        this.LAYER_TYPES = data.LAYER_TYPES

        // init with carto ID
        const {cartoId} = cartoInfo
        this._info = {
            ...defaultCartoLayerInfo,
            "id": cartoId,
            "source-layer": cartoId,
            "source": cartoId
        }

        // set up defaults props based on layer-type
        this._generateTypeDefaults(cartoInfo)

        // any properties that we want to be able to override over layer-type based defaults
        const {minzoom, maxzoom} = cartoInfo
        if (minzoom) this._info['minzoom'] = minzoom
        if (maxzoom) this._info['maxzoom'] = maxzoom
    }

    getStyling = () => {
        return this._info
    }

    _generateTypeDefaults = ({cartoId, layerType, fillColor, fillOutlineColor, lineColor, lineWidth, iconImage, label, textSizeInPixels, labelColor, hideLabelsUntilThisZoom, hideIconsUntilThisZoom, iconScale}) => {
        switch (layerType) {
            case this.LAYER_TYPES.POLYGON:
                this._info = {
                    ...this._info,
                    "type": "fill",
                    "paint": {
                        "fill-outline-color": ["case",
                            ["boolean", ["feature-state", "hover"], false],
                            POLYGON_HOVER_OUTLINE_COLOR,
                            ["string", ["feature-state", "selected"], 'rgba(0,0,0,0)']
                        ],
                        "fill-color": fillColor || TRANSPARENT,
                    }
                }
                break;
            case this.LAYER_TYPES.LINE:
                this._info = {
                    ...this._info,
                    "type": "line",
                    "paint": {
                        "line-color": lineColor || "black",
                        "line-width": lineWidth || 1,
                    }
                }
                break;
            case this.LAYER_TYPES.OUTLINE: // outlines are not supported atm
                console.error('Layer type ' + layerType + ' is not supported.')
                break;
            case this.LAYER_TYPES.SYMBOL:
                const ZOOM_AT_WHICH_LABELS_APPEAR = isNaN(hideLabelsUntilThisZoom) ? 11 : hideLabelsUntilThisZoom
                const ZOOM_AT_WHICH_ICONS_APPEAR = isNaN(hideIconsUntilThisZoom) ? 7.5 : hideIconsUntilThisZoom
                const _iconScale = iconScale || 1
                this._info = {
                    ...this._info,
                    "type": "symbol",
                    "layout": {
                        // Icon options -------------------------------
                        "icon-image": iconImage,

                        // Icon collision control
                        "icon-allow-overlap": true, // If true, THE ICON will be visible even if it collides with other previously drawn symbols
                        "icon-ignore-placement": true, // If true, OTHER SYMBOLS can be visible even if they collide with the icon.
                        
                        "icon-size": [
                            "interpolate",
                            ["exponential", 1.36],
                            ["zoom"],
                            ZOOM_AT_WHICH_ICONS_APPEAR, // zoom level for visuals (small = zoomed out)
                            0.1 * _iconScale, // size of icon at previous, zoomed out level
                            15, // zoom level for visuals (big = zoomed in)
                            0.8 * _iconScale // size at zoomed in, change to a bigger zoom to slow icon size growth
                        ],

                        // Text collision control
                        "text-allow-overlap": false, // If true, THE TEXT will be visible even if it collides with other previously drawn symbols.
                        "text-ignore-placement": true, // If true, OTHER SYMBOLS can be visible even if they collide with the text.
                        "text-optional": true, // If true, icons will display without their corresponding text when the text collides with other symbols and the icon does not.

                        // Text options -------------------------------
                        "text-field": ["step", ["zoom"], '', ZOOM_AT_WHICH_LABELS_APPEAR, label ? ['get', label] : ''],
                        "text-anchor": "top",
                        "text-offset": [0,0.5],
                        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Regular"],
                        "text-size": textSizeInPixels || 16,
                        "text-line-height": 1.1,
                        "text-max-width": 30,
                    },
                    // Color settings -------------------------------
                    "paint": {
                        "text-color": labelColor || "hsl(0,0%,35.3%)",
                        "text-halo-color": "hsl(0,0%,100%)",
                        "text-halo-width": 1.25,
    
                        "icon-opacity": ["case",
                            ["boolean", ["feature-state", "hover"], false],
                            1,
                            0.7
                        ]
                    },
                    // Others -------------------------------
                    "minzoom": ZOOM_AT_WHICH_ICONS_APPEAR
                }
                break;

            default:
                console.error('Layer type doesnt exist', cartoId, layerType)
        }
    }
}

export const getStylingForCartoLayer = (cartoInfo) => {
    const i = new CartoLayerDefaultInfo(cartoInfo)
    return i.getStyling()
}

// =====================================================================
// class MapboxLayerInfo
// Helper info struct to represent the Mapbox styling for a layer
// =====================================================================
class MapboxLayerInfo {
    constructor(data) {
        this._id = data['id']
        this._data = data
    }

    get id() { return this._id }
    get data() { return this._data }
}

// =====================================================================
// class MapboxStyleManager
// Singleton manager that creates and holds all Mapbox style properties for layers
// this class should only be used in DataManager
// =====================================================================
export default class MapboxStyleManager {

    constructor(data) {
        this._cartoLayerStyles = {}

        this._basicMapStyle = fromJS(data.basicMapStyle)
        this._detailedMapStyle = fromJS(data.detailedMapStyle)
        this._satelliteMapStyle = fromJS(data.satelliteMapStyle)

        this._baseLayers = {}
        this._baseLayers[BASE_MAP_LAYERS.BASIC] = data.basicMapStyle.layers.map(l => new MapboxLayerInfo(l))
        this._baseLayers[BASE_MAP_LAYERS.DETAILED] = data.detailedMapStyle.layers.map(l => new MapboxLayerInfo(l))
        this._baseLayers[BASE_MAP_LAYERS.SATELLITE] = data.satelliteMapStyle.layers.map(l => new MapboxLayerInfo(l))

        this._data = data
        this._cartoLayers = tableWithSameValue(data.LAYER_NAMES, true)
    }

    defaultMapStyle = () => {
        return this._basicMapStyle
    }

    detailedMapStyle = () => {
        return this._detailedMapStyle
    }

    // wrapper to create a VisualizedDataLayer object for storymaps
    createLayerPaintProperty = (layerInfo) => {
        return new LayerPaintProperty(layerInfo)
    }

    // create carto layers when importing list-of-possible-layers.yaml
    createCartoLayer = (cartoLayerId, cartoInfo) => {
        this._cartoLayerStyles[cartoLayerId] = new CartoLayerDefaultInfo(cartoInfo, this._data)
    }

    // get default style props for a carto style when fetching the style from Carto
    getStyleForCartoLayer = (cartoLayerId) => {
        return this._cartoLayerStyles[cartoLayerId].getStyling()
    }

    swapBaseLayers = (map, oldBase, newBase) => {
        this._baseLayers[oldBase].forEach(layer => {
            const existingLayer = map.getLayer(layer.id)
            if (existingLayer)
                map.removeLayer(layer.id)
        })

        const layers = map.getStyle().layers
        const lastCartoId = layers.length > 0 ? layers[0].id : undefined
        this._baseLayers[newBase].forEach(layer => {
            map.addLayer(layer.data, lastCartoId)
        })
    }
}
